import API_BASE from '../../utils/api';
import { formatLocalDate } from '../../utils/date';
import React, { useState, useEffect } from "react";
import { useAuth } from "../../context/AuthContext";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import Sidebar from "../../components/Sidebar";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import ProfileDropdown from "../../components/ProfileDropdown";
import Tabs from "../../components/Tabs";
import Modal from "../../components/Modal";
import QrScannerModal from "../../components/QrScannerModal";
import BulkAttendanceImportModal from "../../components/BulkAttendanceImportModal";
import AttendanceReportsTab from "../../components/AttendanceReportsTab";

const AdminAttendance = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState("mark");
  const [classes, setClasses] = useState([]);
  const [selectedClass, setSelectedClass] = useState("");
  const [selectedClassDetails, setSelectedClassDetails] = useState(null);
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [attendanceData, setAttendanceData] = useState([]);
  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const [stats, setStats] = useState({
    total: 0,
    present: 0,
    absent: 0,
    late: 0,
    halfDay: 0,
    onLeave: 0,
    percentage: 0,
  });

  const [classStats, setClassStats] = useState([]);

  // Period-wise marking
  const [periods, setPeriods] = useState([]);
  const [period, setPeriod] = useState(0);
  const [showScanner, setShowScanner] = useState(false);
  const [qrModal, setQrModal] = useState(null);
  const [generatingQr, setGeneratingQr] = useState(false);
  const [showBulkImport, setShowBulkImport] = useState(false);

  useEffect(() => {
    fetchClasses();
  }, []);

  useEffect(() => {
    if (selectedClass) {
      setLoading(true);
      Promise.all([
        fetchClassDetails(),
        fetchStudents(),
        fetchAttendance(),
        fetchPeriods()
      ]).finally(() => setLoading(false));
    }
  }, [selectedClass, selectedDate, period]);

  const fetchPeriods = async () => {
    try {
      const res = await axios.get(`${API_BASE}/attendance/periods/${selectedClass}?date=${formatLocalDate(selectedDate)}`);
      setPeriods(res.data);
    } catch (_) {
      setPeriods([]);
    }
  };

  const fetchClasses = async () => {
    try {
      const response = await axios.get(`${API_BASE}/classes`);
      const classesData = Array.isArray(response.data) ? response.data : [];
      setClasses(classesData);

      if (classesData.length > 0) {
        setSelectedClass(classesData[0]._id);
        setSelectedClassDetails(classesData[0]);
      }
    } catch (error) {
      console.error("Error fetching classes:", error);
      setError("Failed to fetch classes");
    }
  };

  const fetchClassDetails = async () => {
    try {
      const response = await axios.get(
        `${API_BASE}/classes/${selectedClass}`,
      );
      setSelectedClassDetails(response.data);
      return response.data;
    } catch (error) {
      console.error("Error fetching class details:", error);
      return null;
    }
  };

  const fetchStudents = async () => {
    try {
      console.log("Fetching students for class:", selectedClass);
      const response = await axios.get(
        `${API_BASE}/classes/${selectedClass}/students`,
      );
      
      console.log("Students response:", response.data);
      
      // Handle different response formats
      let studentsData = [];
      if (Array.isArray(response.data)) {
        studentsData = response.data;
      } else if (response.data && Array.isArray(response.data.students)) {
        studentsData = response.data.students;
      } else if (response.data && response.data.data && Array.isArray(response.data.data)) {
        studentsData = response.data.data;
      }
      
      console.log("Processed students data:", studentsData);
      setStudents(studentsData);

      // Update total students count
      setStats((prev) => ({
        ...prev,
        total: studentsData.length,
      }));
    } catch (error) {
      console.error("Error fetching students:", error.response?.data || error.message);
      setError("Failed to load students. Please try selecting the class again.");
      setStudents([]);
    }
  };

  const fetchAttendance = async () => {
    try {
      const formattedDate = formatLocalDate(selectedDate);
      const response = await axios.get(
        `${API_BASE}/attendance/class/${selectedClass}?date=${formattedDate}&period=${period}`,
      );

      const attendanceRecords = Array.isArray(response.data)
        ? response.data
        : [];
      setAttendanceData(attendanceRecords);

      // Calculate stats based on attendance records
      const present = attendanceRecords.filter(
        (a) => a.status === "present",
      ).length;
      const absent = attendanceRecords.filter(
        (a) => a.status === "absent",
      ).length;
      const late = attendanceRecords.filter((a) => a.status === "late").length;
      const halfDay = attendanceRecords.filter(
        (a) => a.status === "half-day",
      ).length;
      const onLeave = attendanceRecords.filter(
        (a) => a.status === "on-leave",
      ).length;
      const total = students.length || attendanceRecords.length;

      setStats({
        total,
        present,
        absent,
        late,
        halfDay,
        onLeave,
        // Same weighted formula used by the backend's /attendance/summary
        // and the principal attendance view — half-day counts as half a
        // present, so the same class/day reads the same % everywhere.
        percentage:
          total > 0
            ? Math.round(((present + late + halfDay * 0.5) / total) * 100)
            : 0,
      });

      return attendanceRecords;
    } catch (error) {
      console.error("Error fetching attendance:", error);
      setError("Failed to fetch attendance");
      return [];
    }
  };

  const handleMarkAttendance = async (studentId, status) => {
    try {
      const attendanceData = [
        {
          studentId,
          status,
          remarks: "",
        },
      ];

      await axios.post(`${API_BASE}/attendance/mark`, {
        date: formatLocalDate(selectedDate),
        classId: selectedClass,
        attendance: attendanceData,
        period,
      });

      setSuccess(`Attendance marked as ${status}`);
      fetchAttendance(); // Refresh attendance data
      setTimeout(() => setSuccess(""), 3000);
    } catch (error) {
      console.error("Error marking attendance:", error);
      setError("Failed to mark attendance");
    }
  };

  const handleQrResolved = (session) => {
    setShowScanner(false);
    setSelectedClass(session.classId);
    setPeriod(session.period || 0);
    setSelectedDate(new Date(`${session.date}T00:00:00`));
    setSuccess(`Checked in: ${session.className}-${session.section}, Period ${session.period}${session.subject ? ` (${session.subject})` : ""}`);
    setTimeout(() => setSuccess(""), 4000);
  };

  const generateQr = async () => {
    if (!selectedClass) return;
    setGeneratingQr(true);
    try {
      const res = await axios.post(`${API_BASE}/attendance/qr-session`, { classId: selectedClass, period });
      setQrModal(res.data);
    } catch (err) {
      setError(err.response?.data?.error || "Failed to generate QR code");
    } finally {
      setGeneratingQr(false);
    }
  };

  const exportToCSV = () => {
    const headers = ["Roll No", "Student Name", "Status"];

    // Create a map of attendance records for quick lookup
    const attendanceMap = {};
    attendanceData.forEach((record) => {
      if (record.studentId && record.studentId._id) {
        attendanceMap[record.studentId._id] = record.status;
      }
    });

    // Generate CSV data for all students
    const csvData = students.map((student) => [
      student.rollNumber || "N/A",
      student.name || "N/A",
      attendanceMap[student._id] || "Not Marked",
    ]);

    const csvContent = [headers, ...csvData]
      .map((row) => row.join(","))
      .join("\n");

    const blob = new Blob([csvContent], { type: "text/csv" });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `attendance_${selectedClassDetails?.className || "class"}_${formatLocalDate(selectedDate)}.csv`;
    a.click();
  };

  const getStudentAttendanceStatus = (studentId) => {
    const record = attendanceData.find((r) => r.studentId?._id === studentId);
    return record ? record.status : "Not Marked";
  };

  const handleLogout = () => {
    logout();
    navigate("/login");
  };

  if (loading) {
    return (
      <div className="flex h-screen bg-gray-100">
        <Sidebar />
        <div className="flex-1 flex items-center justify-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex h-screen bg-gray-100">
      <Sidebar />
      <div className="flex-1 overflow-auto">
        <nav className="bg-white shadow-sm sticky top-0 z-10">
          <div className="px-6 py-4 flex justify-between items-center">
            <h1 className="text-2xl font-semibold text-gray-800">
              Attendance Overview
            </h1>
            <ProfileDropdown />
          </div>
        </nav>

        <div className="p-6">
          {/* Status Messages */}
          {error && (
            <div className="mb-6 p-4 bg-red-100 text-red-700 rounded-lg border border-red-400 flex justify-between items-center">
              <span>{error}</span>
              <button
                onClick={() => {
                  setError("");
                  if (selectedClass) {
                    setLoading(true);
                    Promise.all([
                      fetchClassDetails(),
                      fetchStudents(),
                      fetchAttendance()
                    ]).finally(() => setLoading(false));
                  }
                }}
                className="bg-red-600 text-white px-4 py-1 rounded hover:bg-red-700 text-sm"
              >
                Retry
              </button>
            </div>
          )}
          {success && (
            <div className="mb-6 p-4 bg-green-100 text-green-700 rounded-lg border border-green-400">
              {success}
            </div>
          )}

          <div className="mb-6">
            <Tabs
              tabs={[{ key: "mark", label: "Mark Attendance" }, { key: "reports", label: "Reports & Audit" }]}
              active={activeTab}
              onChange={setActiveTab}
            />
          </div>

          {activeTab === "reports" && <AttendanceReportsTab classes={classes} />}

          {activeTab === "mark" && (
          <>
          {/* Filters */}
          <div className="bg-white rounded-lg shadow p-6 mb-6">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Select Class
                </label>
                <select
                  value={selectedClass}
                  onChange={(e) => {
                    setError("");
                    setSelectedClass(e.target.value);
                  }}
                  className="w-full border rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                >
                  {classes.map((cls) => (
                    <option key={cls._id} value={cls._id}>
                      Class {cls.className} - Section {cls.section} (
                      {cls.students?.length || 0} students)
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Select Date
                </label>
                <DatePicker
                  selected={selectedDate}
                  onChange={(date) => setSelectedDate(date)}
                  className="w-full border rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  dateFormat="yyyy-MM-dd"
                  maxDate={new Date()}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Period
                </label>
                <select
                  value={period}
                  onChange={(e) => setPeriod(Number(e.target.value))}
                  className="w-full border rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                >
                  <option value={0}>Whole Day / Homeroom</option>
                  {periods.length > 0
                    ? periods.map((p) => (
                        <option key={p.periodNumber} value={p.periodNumber}>
                          Period {p.periodNumber} — {p.subject}
                        </option>
                      ))
                    : Array.from({ length: 8 }, (_, i) => i + 1).map((n) => (
                        <option key={n} value={n}>Period {n}</option>
                      ))}
                </select>
              </div>
              <div className="flex items-end">
                <button
                  onClick={exportToCSV}
                  className="bg-green-600 text-white px-4 py-2 rounded hover:bg-green-700 transition flex items-center"
                  disabled={students.length === 0}
                >
                  <svg
                    className="w-4 h-4 mr-2"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4"
                    />
                  </svg>
                  Export to CSV
                </button>
              </div>
            </div>
            <div className="flex flex-wrap gap-2 mt-4 pt-4 border-t">
              <button
                onClick={() => setShowScanner(true)}
                className="bg-white border border-gray-300 text-gray-700 px-4 py-2 rounded-lg hover:bg-gray-50 text-sm"
              >
                📷 Scan Class QR
              </button>
              <button
                onClick={generateQr}
                disabled={generatingQr || !selectedClass}
                className="bg-white border border-gray-300 text-gray-700 px-4 py-2 rounded-lg hover:bg-gray-50 text-sm disabled:opacity-50"
              >
                {generatingQr ? "Generating…" : "🔳 Generate QR"}
              </button>
              <button
                onClick={() => setShowBulkImport(true)}
                className="bg-white border border-gray-300 text-gray-700 px-4 py-2 rounded-lg hover:bg-gray-50 text-sm"
              >
                📥 Bulk Import
              </button>
            </div>
          </div>

          {/* Class Info Bar */}
          {selectedClassDetails && (
            <div className="bg-indigo-50 rounded-lg p-4 mb-6">
              <div className="flex justify-between items-center">
                <div>
                  <p className="text-indigo-700 font-medium">
                    Class {selectedClassDetails.className} - Section{" "}
                    {selectedClassDetails.section}
                  </p>
                  <p className="text-sm text-indigo-600">
                    Academic Year: {selectedClassDetails.academicYear}
                  </p>
                </div>
                <div className="text-right">
                  <p className="text-indigo-700 font-medium">
                    Total Students: {students.length}
                  </p>
                  <p className="text-sm text-indigo-600">
                    {attendanceData.length} marked,{" "}
                    {students.length - attendanceData.length} pending
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Statistics Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-7 gap-4 mb-6">
            <div className="bg-white rounded-lg shadow p-4">
              <p className="text-xs text-gray-500">Total Students</p>
              <p className="text-2xl font-bold">{stats.total}</p>
            </div>
            <div className="bg-white rounded-lg shadow p-4">
              <p className="text-xs text-gray-500">Present</p>
              <p className="text-2xl font-bold text-green-600">
                {stats.present}
              </p>
            </div>
            <div className="bg-white rounded-lg shadow p-4">
              <p className="text-xs text-gray-500">Absent</p>
              <p className="text-2xl font-bold text-red-600">{stats.absent}</p>
            </div>
            <div className="bg-white rounded-lg shadow p-4">
              <p className="text-xs text-gray-500">Late</p>
              <p className="text-2xl font-bold text-yellow-600">{stats.late}</p>
            </div>
            <div className="bg-white rounded-lg shadow p-4">
              <p className="text-xs text-gray-500">Half Day</p>
              <p className="text-2xl font-bold text-blue-600">
                {stats.halfDay}
              </p>
            </div>
            <div className="bg-white rounded-lg shadow p-4">
              <p className="text-xs text-gray-500">On Leave</p>
              <p className="text-2xl font-bold text-purple-600">
                {stats.onLeave}
              </p>
            </div>
            <div className="bg-white rounded-lg shadow p-4">
              <p className="text-xs text-gray-500">Attendance %</p>
              <p className="text-2xl font-bold text-indigo-600">
                {stats.percentage}%
              </p>
            </div>
          </div>

          {/* Attendance Summary Chart */}
          <div className="bg-white rounded-lg shadow p-6 mb-6">
            <h2 className="text-lg font-semibold mb-4">Attendance Summary</h2>
            <div className="space-y-3">
              <div>
                <div className="flex justify-between text-sm mb-1">
                  <span>Present</span>
                  <span className="font-medium">{stats.present} students</span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2.5">
                  <div
                    className="bg-green-500 h-2.5 rounded-full"
                    style={{
                      width:
                        stats.total > 0
                          ? `${(stats.present / stats.total) * 100}%`
                          : "0%",
                    }}
                  ></div>
                </div>
              </div>
              <div>
                <div className="flex justify-between text-sm mb-1">
                  <span>Absent</span>
                  <span className="font-medium">{stats.absent} students</span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2.5">
                  <div
                    className="bg-red-500 h-2.5 rounded-full"
                    style={{
                      width:
                        stats.total > 0
                          ? `${(stats.absent / stats.total) * 100}%`
                          : "0%",
                    }}
                  ></div>
                </div>
              </div>
              <div>
                <div className="flex justify-between text-sm mb-1">
                  <span>Late</span>
                  <span className="font-medium">{stats.late} students</span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2.5">
                  <div
                    className="bg-yellow-500 h-2.5 rounded-full"
                    style={{
                      width:
                        stats.total > 0
                          ? `${(stats.late / stats.total) * 100}%`
                          : "0%",
                    }}
                  ></div>
                </div>
              </div>
            </div>
          </div>

          {/* Attendance Table */}
          <div className="bg-white rounded-lg shadow overflow-hidden">
            <div className="px-6 py-4 border-b flex justify-between items-center">
              <h2 className="text-lg font-semibold">Attendance Details</h2>
              <div className="text-sm text-gray-500">
                {attendanceData.length} of {students.length} students marked
              </div>
            </div>

            {students.length === 0 ? (
              <div className="p-12 text-center">
                <span className="text-6xl mb-4 block">👥</span>
                <p className="text-gray-500">No students found in this class</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Roll No
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Student Name
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Status
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Quick Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {students.map((student) => {
                      const status = getStudentAttendanceStatus(student._id);
                      return (
                        <tr key={student._id} className="hover:bg-gray-50">
                          <td className="px-6 py-4 whitespace-nowrap font-medium">
                            {student.rollNumber}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            {student.name}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span
                              className={`px-3 py-1 rounded-full text-xs font-semibold ${
                                status === "present"
                                  ? "bg-green-100 text-green-800"
                                  : status === "absent"
                                    ? "bg-red-100 text-red-800"
                                    : status === "late"
                                      ? "bg-yellow-100 text-yellow-800"
                                      : status === "half-day"
                                        ? "bg-blue-100 text-blue-800"
                                        : status === "on-leave"
                                          ? "bg-purple-100 text-purple-800"
                                          : "bg-gray-100 text-gray-800"
                              }`}
                            >
                              {status}
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            {status === "Not Marked" && (
                              <div className="flex space-x-2">
                                <button
                                  onClick={() =>
                                    handleMarkAttendance(student._id, "present")
                                  }
                                  className="text-xs bg-green-500 text-white px-2 py-1 rounded hover:bg-green-600"
                                >
                                  Present
                                </button>
                                <button
                                  onClick={() =>
                                    handleMarkAttendance(student._id, "absent")
                                  }
                                  className="text-xs bg-red-500 text-white px-2 py-1 rounded hover:bg-red-600"
                                >
                                  Absent
                                </button>
                                <button
                                  onClick={() =>
                                    handleMarkAttendance(student._id, "late")
                                  }
                                  className="text-xs bg-yellow-500 text-white px-2 py-1 rounded hover:bg-yellow-600"
                                >
                                  Late
                                </button>
                                <button
                                  onClick={() =>
                                    handleMarkAttendance(student._id, "half-day")
                                  }
                                  className="text-xs bg-blue-500 text-white px-2 py-1 rounded hover:bg-blue-600"
                                >
                                  Half Day
                                </button>
                                <button
                                  onClick={() =>
                                    handleMarkAttendance(student._id, "on-leave")
                                  }
                                  className="text-xs bg-purple-500 text-white px-2 py-1 rounded hover:bg-purple-600"
                                >
                                  On Leave
                                </button>
                              </div>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
          </>
          )}
        </div>
      </div>

      {showScanner && (
        <QrScannerModal onClose={() => setShowScanner(false)} onResolved={handleQrResolved} />
      )}

      {qrModal && (
        <Modal
          title="Classroom Attendance QR"
          subtitle={`${qrModal.className} - ${qrModal.section} · Period ${qrModal.period} · expires in ${qrModal.expiresInMinutes} min`}
          onClose={() => setQrModal(null)}
        >
          <div className="flex flex-col items-center gap-4">
            <img src={qrModal.qrImage} alt="Attendance QR code" className="w-64 h-64" />
            <a
              href={qrModal.qrImage}
              download={`attendance-qr-${qrModal.className}-${qrModal.section}-p${qrModal.period}.png`}
              className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 text-sm"
            >
              Download / Print
            </a>
            <p className="text-xs text-gray-500 text-center">
              Display this in the classroom. Scanning it opens this class/period's attendance screen.
            </p>
          </div>
        </Modal>
      )}

      {showBulkImport && (
        <BulkAttendanceImportModal
          onClose={() => setShowBulkImport(false)}
          onImported={() => { fetchAttendance(); }}
        />
      )}
    </div>
  );
};

export default AdminAttendance;
